import { Controller } from "@hotwired/stimulus"

export default class extends Controller {
  static targets = ["mainImage", "title", "counter", "loader", "buffer", "thumbnail", "carousel"]
  static values = { galleryId: String }

  declare readonly mainImageTarget: HTMLImageElement
  declare readonly titleTarget: HTMLElement
  declare readonly counterTarget: HTMLElement
  declare readonly loaderTarget: HTMLElement
  declare readonly thumbnailTargets: HTMLElement[]
  
  private images: Array<{ source: string, title: string, index: number }> = []
  private currentIndex: number = 0

  connect() {
    this.collectImages()
    this.updateUI()
  }

  collectImages() {
    const galleryItems = this.thumbnailTargets.length > 0 ? this.thumbnailTargets : document.querySelectorAll('[data-gallery-source]')
    this.images = Array.from(galleryItems).map((item, index) => ({
      source: (item as HTMLElement).dataset.gallerySource || "",
      title: (item as HTMLElement).dataset.galleryTitle || "",
      index: index
    }))

    // Initialize currentIndex based on the currently displayed src if possible
    const currentSrc = this.mainImageTarget.src
    const foundIndex = this.images.findIndex(img => currentSrc.includes(img.source))
    if (foundIndex !== -1) this.currentIndex = foundIndex
  }

  select(event: MouseEvent) {
    event.preventDefault()
    const clickedElement = event.currentTarget as HTMLElement
    this.currentIndex = parseInt(clickedElement.dataset.galleryIndex || "0")
    this.showImage()
  }

  next() {
    if (this.images.length === 0) this.collectImages()
    this.currentIndex = (this.currentIndex + 1) % this.images.length
    this.showImage()
  }

  prev() {
    if (this.images.length === 0) this.collectImages()
    this.currentIndex = (this.currentIndex - 1 + this.images.length) % this.images.length
    this.showImage()
  }

  showImage() {
    const image = this.images[this.currentIndex]
    if (!image) return

    this.mainImageTarget.style.opacity = "0.3"
    this.loaderTarget.classList.remove("hidden")
    
    const newImg = new Image()
    newImg.src = image.source
    newImg.onload = () => {
      this.mainImageTarget.src = image.source
      this.mainImageTarget.style.opacity = "1"
      this.loaderTarget.classList.add("hidden")
      this.updateUI()
      this.preloadAdjacent()
    }
  }

  updateUI() {
    const image = this.images[this.currentIndex]
    if (image) {
      this.titleTarget.textContent = image.title
      this.counterTarget.textContent = `Image ${this.currentIndex + 1} / ${this.images.length}`
      
      // Update thumbnails state
      this.thumbnailTargets.forEach((thumb, idx) => {
        const img = thumb.querySelector('img')
        if (idx === this.currentIndex) {
          thumb.classList.add('ring-2', 'ring-primary', 'ring-offset-4', 'ring-offset-surface')
          if (img) img.classList.remove('grayscale', 'opacity-60')
          thumb.scrollIntoView({ behavior: 'smooth', block: 'nearest', inline: 'center' })
        } else {
          thumb.classList.remove('ring-2', 'ring-primary', 'ring-offset-4', 'ring-offset-surface')
          if (img) img.classList.add('grayscale', 'opacity-60')
        }
      })
    }
  }

  preloadAdjacent() {
    if (this.images.length === 0) return
    const nextIndex = (this.currentIndex + 1) % this.images.length
    const prevIndex = (this.currentIndex - 1 + this.images.length) % this.images.length
    
    this.preload(this.images[nextIndex].source)
    this.preload(this.images[prevIndex].source)
  }

  preload(src: string) {
    if (!src) return
    const img = new Image()
    img.src = src
  }

  scrollThumbNext() {
    this.carouselTarget.scrollBy({ left: this.carouselTarget.clientWidth * 0.8, behavior: 'smooth' })
  }

  scrollThumbPrev() {
    this.carouselTarget.scrollBy({ left: -this.carouselTarget.clientWidth * 0.8, behavior: 'smooth' })
  }
}
